import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { InboxResponse, InboxUnreadCountResponse } from '@cosvision/shared';
import * as inboxApi from '../lib/api/inbox.js';
import { markAlertRead } from '../lib/api/alerts.js';
import * as notificationsApi from '../lib/api/notifications.js';
import { qk } from '../lib/query-keys.js';

const BELL_POLL_MS = 30_000;

export function useInboxUnreadQuery(enabled: boolean): UseQueryResult<InboxUnreadCountResponse> {
  return useQuery({
    queryKey: qk.inbox.unreadCount(),
    queryFn: inboxApi.getInboxUnreadCount,
    enabled,
    refetchInterval: BELL_POLL_MS,
    refetchIntervalInBackground: false,
    staleTime: 10_000,
  });
}

export function useInboxListQuery(enabled: boolean): UseQueryResult<InboxResponse> {
  return useQuery({
    queryKey: qk.inbox.list(),
    queryFn: inboxApi.getInbox,
    enabled,
    staleTime: 10_000,
  });
}

/**
 * Optimistic mark-read on a single notification. Patches the cached inbox
 * + notification queries immediately so the count badge updates without a
 * round-trip, and rolls back if the request fails.
 */
export function useMarkNotificationReadOptimistic() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => notificationsApi.markNotificationRead(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: qk.inbox.all });
      const prevList = qc.getQueryData<InboxResponse>(qk.inbox.list());
      const prevCount = qc.getQueryData<InboxUnreadCountResponse>(qk.inbox.unreadCount());
      if (prevList) {
        qc.setQueryData<InboxResponse>(qk.inbox.list(), {
          ...prevList,
          activity: prevList.activity.map((n) => (n.id === id ? { ...n, isRead: true } : n)),
        });
      }
      if (prevCount) {
        const matched = prevList?.activity.find((n) => n.id === id);
        const wasUnread = matched ? !matched.isRead : false;
        if (wasUnread) {
          const nextActivity = Math.max(0, prevCount.activity - 1);
          qc.setQueryData<InboxUnreadCountResponse>(qk.inbox.unreadCount(), {
            ...prevCount,
            activity: nextActivity,
            total: nextActivity + prevCount.alerts,
          });
        }
      }
      return { prevList, prevCount };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prevList) qc.setQueryData(qk.inbox.list(), ctx.prevList);
      if (ctx?.prevCount) qc.setQueryData(qk.inbox.unreadCount(), ctx.prevCount);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: qk.inbox.all });
      void qc.invalidateQueries({ queryKey: qk.notifications.all });
    },
  });
}

/** Optimistic mark-read on a single financial alert (CO-only). */
export function useMarkAlertReadOptimistic() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => markAlertRead(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: qk.inbox.all });
      const prevList = qc.getQueryData<InboxResponse>(qk.inbox.list());
      const prevCount = qc.getQueryData<InboxUnreadCountResponse>(qk.inbox.unreadCount());
      if (prevList) {
        qc.setQueryData<InboxResponse>(qk.inbox.list(), {
          ...prevList,
          alerts: prevList.alerts.map((a) => (a.id === id ? { ...a, isRead: true } : a)),
        });
      }
      if (prevCount) {
        const matched = prevList?.alerts.find((a) => a.id === id);
        const wasUnread = matched ? !matched.isRead : false;
        if (wasUnread) {
          const nextAlerts = Math.max(0, prevCount.alerts - 1);
          qc.setQueryData<InboxUnreadCountResponse>(qk.inbox.unreadCount(), {
            ...prevCount,
            alerts: nextAlerts,
            total: prevCount.activity + nextAlerts,
          });
        }
      }
      return { prevList, prevCount };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prevList) qc.setQueryData(qk.inbox.list(), ctx.prevList);
      if (ctx?.prevCount) qc.setQueryData(qk.inbox.unreadCount(), ctx.prevCount);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: qk.inbox.all });
      void qc.invalidateQueries({ queryKey: ['alerts'] });
    },
  });
}

/** Mark all activity notifications read in one shot. */
export function useMarkAllActivityReadMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: notificationsApi.markAllNotificationsRead,
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: qk.inbox.all });
      void qc.invalidateQueries({ queryKey: qk.notifications.all });
    },
  });
}
